import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Zap, Gauge } from 'lucide-react'

const SpeedControl = ({
  isConnected,
  currentSpeed,
  sendCarCommand
}) => {
  const [localSpeed, setLocalSpeed] = useState(currentSpeed || 512)
  const maxSpeed = 1023

  const handleSpeedChange = (newSpeed) => {
    setLocalSpeed(newSpeed)
    if (isConnected) {
      sendCarCommand('car_speed', newSpeed)
    }
  }

  const getSpeedPercentage = () => {
    return Math.round((localSpeed / maxSpeed) * 100)
  }

  const getSpeedColor = () => {
    const percentage = getSpeedPercentage()
    if (percentage < 30) return 'text-success-400'
    if (percentage < 70) return 'text-warning-400'
    return 'text-danger-400'
  }

  const getSpeedLabel = () => {
    const percentage = getSpeedPercentage()
    if (percentage < 30) return 'Slow'
    if (percentage < 70) return 'Medium'
    return 'Fast'
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Zap className="w-5 h-5 text-primary-400" />
          <h3 className="text-lg font-semibold">Speed Control</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Gauge className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-400">
            {getSpeedLabel()} ({getSpeedPercentage()}%)
          </span>
        </div>
      </div>

      {/* Speed Slider */}
      <div className="space-y-3">
        <div className="relative">
          <input
            type="range"
            min="0"
            max={maxSpeed}
            value={localSpeed}
            onChange={(e) => handleSpeedChange(parseInt(e.target.value))}
            disabled={!isConnected}
            className="w-full h-3 bg-secondary-600 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, 
                ${getSpeedPercentage() < 30 ? '#22c55e' : getSpeedPercentage() < 70 ? '#f59e0b' : '#ef4444'} 
                ${getSpeedPercentage()}%, 
                #475569 ${getSpeedPercentage()}%)`
            }}
          />
          
          {/* Speed markers */}
          <div className="flex justify-between text-xs text-gray-400 mt-2">
            <span>0</span>
            <span>Slow</span>
            <span>Medium</span>
            <span>Fast</span>
            <span>{maxSpeed}</span>
          </div>
        </div>

        {/* Speed Display */}
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div className={`text-2xl font-bold ${getSpeedColor()}`}>
              {localSpeed}
            </div>
            <div className="text-xs text-gray-400">Current Speed</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-semibold text-primary-400">
              {getSpeedPercentage()}%
            </div>
            <div className="text-xs text-gray-400">Percentage</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-semibold text-secondary-400">
              {getSpeedLabel()}
            </div>
            <div className="text-xs text-gray-400">Speed Level</div>
          </div>
        </div>
      </div>

      {/* Quick Speed Buttons */}
      <div className="grid grid-cols-3 gap-2">
        <motion.button
          whileHover={isConnected ? { scale: 1.05 } : {}}
          whileTap={isConnected ? { scale: 0.95 } : {}}
          onClick={() => handleSpeedChange(256)}
          disabled={!isConnected}
          className="btn-secondary text-sm py-2"
        >
          Slow (25%)
        </motion.button>
        
        <motion.button
          whileHover={isConnected ? { scale: 1.05 } : {}}
          whileTap={isConnected ? { scale: 0.95 } : {}}
          onClick={() => handleSpeedChange(512)}
          disabled={!isConnected}
          className="btn-secondary text-sm py-2"
        >
          Medium (50%)
        </motion.button>
        
        <motion.button
          whileHover={isConnected ? { scale: 1.05 } : {}}
          whileTap={isConnected ? { scale: 0.95 } : {}}
          onClick={() => handleSpeedChange(768)}
          disabled={!isConnected}
          className="btn-secondary text-sm py-2"
        >
          Fast (75%)
        </motion.button>
      </div>

      {/* Connection Status */}
      {!isConnected && (
        <div className="text-center p-3 bg-secondary-500/20 rounded-lg">
          <p className="text-sm text-secondary-300">
            Connect to a device to control speed
          </p>
        </div>
      )}
    </div>
  )
}

export default SpeedControl 